import { assign, createMachine } from 'xstate';

export interface Step {
  step: string;
  title: CreateDestinationTitle;
  isSubStep: boolean;
}

export enum CreateDestinationTitle {
  INFO = 'createDestination:form.details',
  TRANSLATIONS = 'createDestination:form.translations',
  DIRECTION_TIPS = 'createDestination:form.directionTips',
  HOSTS = 'createDestination:form.hosts',
  RECEPTIONS = 'createDestination:form.receptions',
  KIOSK = 'createDestination:form.kiosk',
  CONFIRMATION = 'createDestination:form.confirmation',
}

export interface CreateDestinationContext {
  step: string;
  content: CreateDestinationTitle;
  hasOrganizationKiosk?: boolean;
  skipHostsStep?: boolean;
}

export enum CreateDestinationStates {
  INFO = 'info',
  TRANSLATIONS = 'translations',
  DIRECTION_TIPS = 'directionTips',
  HOSTS = 'hosts',
  RECEPTIONS = 'receptions',
  KIOSK = 'kiosk',
  CONFIRMATION = 'confirmation',
}

export type CreateDestinationEvent =
  | {
      type: 'NEXT';
    }
  | {
      type: 'BACK';
    };

export const createDestinationMachine = createMachine({
  types: {} as {
    context: CreateDestinationContext;
    states: {
      [CreateDestinationStates.INFO]: unknown;
      [CreateDestinationStates.TRANSLATIONS]: unknown;
      [CreateDestinationStates.DIRECTION_TIPS]: unknown;
      [CreateDestinationStates.HOSTS]: unknown;
      [CreateDestinationStates.RECEPTIONS]: unknown;
      [CreateDestinationStates.KIOSK]: unknown;
      [CreateDestinationStates.CONFIRMATION]: unknown;
    };
    events: CreateDestinationEvent;
  },
  id: 'create-poll',
  context: ({ input }: { input: CreateDestinationContext }) => ({
    step: input?.step || '1',
    content: CreateDestinationTitle.INFO,
    hasOrganizationKiosk: input?.hasOrganizationKiosk,
    skipHostsStep: input?.skipHostsStep,
  }),
  initial: CreateDestinationStates.INFO,
  states: {
    [CreateDestinationStates.INFO]: {
      on: {
        NEXT: {
          target: CreateDestinationStates.TRANSLATIONS,
          actions: assign({
            step: '2',
            content: CreateDestinationTitle.TRANSLATIONS,
          }),
        },
      },
    },
    [CreateDestinationStates.TRANSLATIONS]: {
      on: {
        NEXT: {
          target: CreateDestinationStates.DIRECTION_TIPS,
          actions: assign({
            step: '3',
            content: CreateDestinationTitle.DIRECTION_TIPS,
          }),
        },
        BACK: {
          target: CreateDestinationStates.INFO,
          actions: assign({
            step: '1',
            content: CreateDestinationTitle.INFO,
          }),
        },
      },
    },
    [CreateDestinationStates.DIRECTION_TIPS]: {
      on: {
        NEXT: [
          {
            target: CreateDestinationStates.RECEPTIONS,
            guard: ({ context }) => context.skipHostsStep === true,
            actions: assign({
              step: '4',
              content: CreateDestinationTitle.RECEPTIONS,
            }),
          },
          {
            target: CreateDestinationStates.HOSTS,
            actions: assign({
              step: '4',
              content: CreateDestinationTitle.HOSTS,
            }),
          },
        ],
        BACK: {
          target: CreateDestinationStates.TRANSLATIONS,
          actions: assign({
            step: '2',
            content: CreateDestinationTitle.TRANSLATIONS,
          }),
        },
      },
    },
    [CreateDestinationStates.HOSTS]: {
      on: {
        NEXT: {
          target: CreateDestinationStates.RECEPTIONS,
          actions: assign({
            step: '5',
            content: CreateDestinationTitle.RECEPTIONS,
          }),
        },
        BACK: {
          target: CreateDestinationStates.DIRECTION_TIPS,
          actions: assign({
            step: '3',
            content: CreateDestinationTitle.DIRECTION_TIPS,
          }),
        },
      },
    },
    [CreateDestinationStates.RECEPTIONS]: {
      on: {
        NEXT: [
          {
            target: CreateDestinationStates.CONFIRMATION,
            guard: ({ context }) =>
              context.hasOrganizationKiosk === false && context.skipHostsStep === true,
            actions: assign({
              step: '5',
              content: CreateDestinationTitle.CONFIRMATION,
            }),
          },
          {
            target: CreateDestinationStates.CONFIRMATION,
            guard: ({ context }) =>
              context.hasOrganizationKiosk === false && context.skipHostsStep !== true,
            actions: assign({
              step: '6',
              content: CreateDestinationTitle.CONFIRMATION,
            }),
          },
          {
            target: CreateDestinationStates.KIOSK,
            guard: ({ context }) =>
              context.hasOrganizationKiosk === true && context.skipHostsStep === true,
            actions: assign({
              step: '5',
              content: CreateDestinationTitle.KIOSK,
            }),
          },
          {
            target: CreateDestinationStates.KIOSK,
            guard: ({ context }) =>
              context.hasOrganizationKiosk === true && context.skipHostsStep !== true,
            actions: assign({
              step: '6',
              content: CreateDestinationTitle.KIOSK,
            }),
          },
        ],
        BACK: [
          {
            target: CreateDestinationStates.DIRECTION_TIPS,
            guard: ({ context }) => context.skipHostsStep === true,
            actions: assign({
              step: '3',
              content: CreateDestinationTitle.DIRECTION_TIPS,
            }),
          },
          {
            target: CreateDestinationStates.HOSTS,
            actions: assign({
              step: '4',
              content: CreateDestinationTitle.HOSTS,
            }),
          },
        ],
      },
    },
    [CreateDestinationStates.KIOSK]: {
      on: {
        NEXT: [
          {
            target: CreateDestinationStates.CONFIRMATION,
            guard: ({ context }) => context.skipHostsStep === true,
            actions: assign({
              step: '6',
              content: CreateDestinationTitle.CONFIRMATION,
            }),
          },
          {
            target: CreateDestinationStates.CONFIRMATION,
            actions: assign({
              step: '7',
              content: CreateDestinationTitle.CONFIRMATION,
            }),
          },
        ],
        BACK: {
          target: CreateDestinationStates.RECEPTIONS,
          actions: assign({
            step: '5',
            content: CreateDestinationTitle.RECEPTIONS,
          }),
        },
      },
    },
    [CreateDestinationStates.CONFIRMATION]: {
      on: {
        BACK: [
          {
            target: CreateDestinationStates.RECEPTIONS,
            guard: ({ context }) =>
              context.hasOrganizationKiosk === false && context.skipHostsStep === true,
            actions: assign({
              step: '4',
              content: CreateDestinationTitle.RECEPTIONS,
            }),
          },
          {
            target: CreateDestinationStates.RECEPTIONS,
            guard: ({ context }) =>
              context.hasOrganizationKiosk === false && context.skipHostsStep !== true,
            actions: assign({
              step: '5',
              content: CreateDestinationTitle.RECEPTIONS,
            }),
          },
          {
            target: CreateDestinationStates.KIOSK,
            guard: ({ context }) =>
              context.hasOrganizationKiosk === true && context.skipHostsStep === true,
            actions: assign({
              step: '5',
              content: CreateDestinationTitle.KIOSK,
            }),
          },
          {
            target: CreateDestinationStates.KIOSK,
            guard: ({ context }) =>
              context.hasOrganizationKiosk === true && context.skipHostsStep !== true,
            actions: assign({
              step: '6',
              content: CreateDestinationTitle.KIOSK,
            }),
          },
        ],
      },
    },
  },
});
